/** Leads: where inquiries come from, what they ask about, and where each one stands. */
import { z } from "zod";

export const LEAD_SOURCES = {
  website: "Website",
  facebook: "Facebook",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  google: "Google",
  thumbtack: "Thumbtack",
  referral: "Referral",
  email: "Email signature",
  booking: "Booking page",
  staff: "Added by staff",
  other: "Other",
} as const;
export type LeadSource = keyof typeof LEAD_SOURCES;

/** The channels a tagged link can be made for (the rest are set by ClientFlow). */
export const LINK_SOURCES: LeadSource[] = ["website", "facebook", "instagram", "linkedin", "google", "email"];

/** "How did you hear about us?" on the form, when the link didn't say. */
export const HEARD_FROM: Array<[LeadSource, string]> = [
  ["google", "Google search"],
  ["facebook", "Facebook"],
  ["instagram", "Instagram"],
  ["linkedin", "LinkedIn"],
  ["referral", "A friend or client referred me"],
  ["other", "Other"],
];

export const LEAD_SERVICES = {
  tax_prep: "Tax preparation",
  tax_planning: "Tax planning",
  irs_notice: "IRS or state notice",
  bookkeeping: "Bookkeeping & accounting",
  payroll: "Payroll",
  advisory: "CFO & advisory",
  not_sure: "Not sure yet",
} as const;
export type LeadService = keyof typeof LEAD_SERVICES;

export const LEAD_STATUSES = {
  new: "New",
  contacted: "Contacted",
  meeting_booked: "Meeting booked",
  onboarding: "Onboarding",
  lost: "Closed",
  spam: "Spam",
} as const;
export type LeadStatusKey = keyof typeof LEAD_STATUSES;
/** Still being worked: shown first, and followed up. */
export const OPEN_LEAD_STATUSES: LeadStatusKey[] = ["new", "contacted", "meeting_booked"];

/**
 * Follow-up times the firm can choose (Firm Settings › Client communication › Leads): a new lead not
 * contacted within this many hours goes on the follow-up list. Firm policy 14: 48 hours by default
 * (was 24 until the owner's decision of 27 October 2026).
 */
export const FOLLOW_UP_HOURS = [24, 48, 72, 96, 120] as const;
export type FollowUpHours = (typeof FOLLOW_UP_HOURS)[number];

export type LeadSettings = {
  /** Who is emailed about each new inquiry (and gets the daily follow-up list). Empty: the owners. */
  notifyUserIds: string[];
  /** Thank the person by email straight away, with the booking link. */
  autoReply: boolean;
  /**
   * A new lead not yet contacted after this many hours goes on the follow-up list. Each lead keeps
   * the time in force when it arrived (leads.follow_up_hours); a change applies to later leads.
   */
  followUpHours: number;
};

export const LEAD_DEFAULTS: LeadSettings = { notifyUserIds: [], autoReply: true, followUpHours: 48 };

export const leadSettingsSchema = z
  .object({
    notifyUserIds: z.array(z.string().uuid()).max(20),
    autoReply: z.boolean(),
    followUpHours: z
      .number()
      .int()
      .refine((v) => (FOLLOW_UP_HOURS as readonly number[]).includes(v), { message: "Choose 24, 48, 72, 96 or 120 hours" }),
  })
  .partial()
  .strict();

/**
 * The firm's lead settings, with defaults. A follow-up time saved before the choices were limited
 * (any whole number of hours from 1 to 168) is kept until someone changes it; anything else is the default.
 */
export function leadSettings(stored: unknown): LeadSettings {
  const s = (stored && typeof stored === "object" ? stored : {}) as Partial<LeadSettings>;
  const h = s.followUpHours;
  return {
    notifyUserIds: Array.isArray(s.notifyUserIds) ? [...s.notifyUserIds] : [],
    autoReply: typeof s.autoReply === "boolean" ? s.autoReply : LEAD_DEFAULTS.autoReply,
    followUpHours: typeof h === "number" && Number.isInteger(h) && h >= 1 && h <= 168 ? h : LEAD_DEFAULTS.followUpHours,
  };
}

/** Business days the firm promises to reply in: one for each started 24 hours of the follow-up time. */
export function replyBusinessDays(followUpHours: number): number {
  return Math.max(1, Math.ceil(followUpHours / 24));
}

/**
 * The promise in the thank-you email and on the inquiry form, from the follow-up time:
 * "within 1 business day" for 24 hours or less, "within 2 business days" up to 48 hours, and so on.
 */
export function replyPromise(followUpHours: number): string {
  const n = replyBusinessDays(followUpHours);
  return `within ${n} business day${n === 1 ? "" : "s"}`;
}

/** The channel a visitor came from: the link's own tag, or a utm_source that names one. */
export function leadSourceFrom(source: string | null | undefined, utmSource?: string | null): LeadSource | null {
  const v = (source || utmSource || "").trim().toLowerCase();
  if (!v) return null;
  if (Object.hasOwn(LEAD_SOURCES, v) && !["booking", "staff", "thumbtack"].includes(v)) return v as LeadSource;
  if (["fb", "meta"].includes(v)) return "facebook";
  if (v === "ig") return "instagram";
  if (["li", "linked_in"].includes(v)) return "linkedin";
  if (["gbp", "google_business", "gmb"].includes(v)) return "google";
  return "other";
}

/** Messages full of links, or links in the name, are almost always spam. */
export function looksLikeSpam(v: { name: string; message: string; company?: string }): boolean {
  const links = (v.message.match(/https?:\/\/|www\./gi) ?? []).length;
  return links > 2 || /https?:\/\/|www\./i.test(v.name) || /https?:\/\/|www\./i.test(v.company ?? "");
}
